import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { FaGamepad, FaStore, FaTrophy, FaNewspaper, FaVideo, FaSignOutAlt, FaBars, FaTimes, FaCrown, FaHistory, FaGlobe, FaBriefcase } from 'react-icons/fa';
import { HiOutlineViewGrid } from 'react-icons/hi';
import { useLanguage } from '../../contexts/LanguageContext';
import { getSiteLogo } from '../../utils/siteLogo';
import { useStaffNames } from '../../utils/staffNames';

const NAV_KEYS = [
    { to: '/', labelKey: 'nav.home', emoji: '🏠' },
    { to: '/games', labelKey: 'nav.games', emoji: '🎮' },
    { to: '/marketplace', labelKey: 'nav.marketplace', emoji: '🛒' },
    { to: '/portfolio', labelKey: 'nav.portfolio', emoji: '🏆' },
    { to: '/news', labelKey: 'nav.news', emoji: '📰' },
    { to: '/live', labelKey: 'nav.live', emoji: '🔴' },
    // Jobboard link is a BUILD-time switch (no Firestore read on every page): set
    // REACT_APP_JOBBOARD_NAV=1 once jobboard_config/main is 'public'. /jobs itself still works without it.
    // `special` = the highlighted menu entry (animated gradient pill + "ใหม่" badge).
    ...(process.env.REACT_APP_JOBBOARD_NAV === '1' ? [{ to: '/jobs', labelKey: 'nav.jobs', emoji: '💼', special: true }] : []),
];

function Navbar({ user, userData, onLoginClick, onLogoutClick, mobileMenuOpen, setMobileMenuOpen, userUnreadCount = 0, adminUnreadCount = 0 }) {
    const location = useLocation();
    const { t, lang, toggleLang } = useLanguage();
    const navItems = NAV_KEYS.map(item => ({ ...item, label: t(item.labelKey) }));

    // Fetch custom logo from Firestore
    const [customLogo, setCustomLogo] = useState(null);
    useEffect(() => {
        getSiteLogo().then(url => { if (url) setCustomLogo(url); });
    }, []);
    const isActive = (path) => location.pathname === path;
    const isAdmin = userData?.role === 'admin' || userData?.role === 'superAdmin';
    const isLoggedIn = user && !user.isAnonymous;
    // Staff with a public nickname (staff_profiles) are shown by it; customers are untouched
    // and trigger no read.
    const staffLabel = useStaffNames({ uids: isAdmin ? [user?.uid] : [] });
    const staffNick = isAdmin ? staffLabel(user?.uid, null) : null;
    const userInitial = (staffNick || userData?.displayName || user?.email || '?').replace(/^#/, '').charAt(0).toUpperCase();
    const userName = staffNick || userData?.displayName || user?.email?.split('@')[0];

    return (
        <>
            {/* ===== DESKTOP NAVBAR ===== */}
            <header
                className="fixed top-0 left-0 right-0 z-50"
                style={{
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.92) 100%)',
                    backdropFilter: 'blur(24px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    {/* lg (1024–1279) is the tight band: the full menu + right section only just fit,
                        so spacing/type are compacted there and relax again at xl. */}
                    <div className="flex items-center h-16 gap-3 xl:gap-4">
                        {/* Logo */}
                        <Link to="/" className="flex items-center flex-shrink-0 group">
                            {customLogo ? (
                                <img
                                    src={customLogo}
                                    alt="Logo"
                                    className="h-[70px] max-w-[160px] lg:max-w-[140px] xl:max-w-[160px] object-contain transition-transform duration-300 group-hover:scale-110"
                                />
                            ) : (
                                <div className="flex items-center gap-2.5">
                                    <div
                                        className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                                        style={{
                                            background: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
                                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                                        }}
                                    >
                                        <FaGamepad className="text-white text-lg" />
                                    </div>
                                    <span className="text-white font-bold text-lg tracking-tight hidden xl:inline" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                        Fastlane<span style={{ color: '#818CF8' }}>Lv.</span>
                                    </span>
                                </div>
                            )}
                        </Link>

                        {/* Desktop Nav Links — Flex center, fills available space */}
                        <nav className="hidden lg:flex items-center justify-center flex-1 gap-0.5">
                            {navItems.map(item => item.special ? (
                                // Highlighted entry: a 1.5px animated gradient frame around a dark pill,
                                // gradient label, a periodic light sweep and a pulsing "ใหม่" badge.
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className="nav-special relative ml-1.5 mr-1 rounded-[10px] p-[1.5px] whitespace-nowrap"
                                    aria-current={isActive(item.to) ? 'page' : undefined}
                                >
                                    <span
                                        className="relative flex items-center gap-1.5 px-2.5 xl:px-3 py-[6.5px] rounded-[8.5px] text-[13px] leading-5 xl:text-sm font-bold overflow-hidden"
                                        style={{
                                            background: isActive(item.to)
                                                ? 'linear-gradient(135deg, rgba(245,158,11,0.32), rgba(236,72,153,0.32), rgba(139,92,246,0.32))'
                                                : 'linear-gradient(135deg, #111827, #1E1B4B)',
                                        }}
                                    >
                                        <FaBriefcase className="text-[12px] nav-special-icon" />
                                        <span className="nav-special-text">{item.label}</span>
                                        <span className="nav-special-shine" aria-hidden="true" />
                                    </span>
                                    <span className="nav-special-badge" aria-hidden="true">{lang === 'th' ? 'ใหม่' : 'NEW'}</span>
                                </Link>
                            ) : (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className="relative px-2 xl:px-3 py-2 rounded-lg text-[13px] leading-5 xl:text-sm font-medium transition-all duration-200 whitespace-nowrap"
                                    style={{
                                        color: isActive(item.to) ? '#fff' : 'rgba(255,255,255,0.55)',
                                        background: isActive(item.to) ? 'rgba(255,255,255,0.1)' : 'transparent',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isActive(item.to)) {
                                            e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isActive(item.to)) {
                                            e.currentTarget.style.color = 'rgba(255,255,255,0.55)';
                                            e.currentTarget.style.background = 'transparent';
                                        }
                                    }}
                                >
                                    {item.label}
                                    {isActive(item.to) && (
                                        <span
                                            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full"
                                            style={{ background: 'linear-gradient(90deg, #818CF8, #6366F1)' }}
                                        />
                                    )}
                                </Link>
                            ))}
                        </nav>

                        {/* Right Section - Desktop. Shrinkable (min-w-0 chain) so that if it is ever
                            short of room the user name truncates instead of the row overflowing;
                            every label is nowrap so Thai text can't break onto a 2nd line. */}
                        <div className="hidden lg:flex items-center gap-2 min-w-0">
                            {isLoggedIn ? (
                                <div className="flex items-center gap-2 min-w-0">
                                    {isAdmin && (
                                        <Link
                                            to="/admin"
                                            title={t('nav.dashboard')}
                                            className="relative flex items-center gap-1.5 px-2.5 xl:px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200"
                                            style={{
                                                color: isActive('/admin') ? '#fff' : '#A5B4FC',
                                                background: isActive('/admin') ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.1)',
                                                border: '1px solid rgba(99, 102, 241, 0.2)',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.25)';
                                                e.currentTarget.style.color = '#fff';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isActive('/admin')) {
                                                    e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
                                                    e.currentTarget.style.color = '#A5B4FC';
                                                }
                                            }}
                                        >
                                            <HiOutlineViewGrid className="text-sm" />
                                            {/* Icon-only at lg (admins have the widest right section); label from xl */}
                                            <span className="sr-only xl:not-sr-only xl:whitespace-nowrap">{t('nav.dashboard')}</span>
                                            {adminUnreadCount > 0 && (
                                                <span style={{
                                                    position: 'absolute', top: '-5px', right: '-5px',
                                                    minWidth: '16px', height: '16px',
                                                    background: '#EF4444', borderRadius: '99px',
                                                    fontSize: '9px', fontWeight: '800', color: '#fff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    padding: '0 3px', boxShadow: '0 0 0 2px rgba(15,23,42,0.95)',
                                                    animation: 'navBadgePulse 2s ease-in-out infinite',
                                                }}>
                                                    {adminUnreadCount > 99 ? '99+' : adminUnreadCount}
                                                </span>
                                            )}
                                        </Link>
                                    )}
                                    <Link
                                        to="/history"
                                        title={t('nav.history')}
                                        className="relative flex items-center gap-1.5 px-2.5 xl:px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200"
                                        style={{
                                            color: isActive('/history') ? '#fff' : 'rgba(255,255,255,0.5)',
                                            background: isActive('/history') ? 'rgba(255,255,255,0.1)' : 'transparent',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isActive('/history')) {
                                                e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
                                                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isActive('/history')) {
                                                e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                                                e.currentTarget.style.background = 'transparent';
                                            }
                                        }}
                                    >
                                        {/* Admins (extra dashboard button) get an icon-only link at lg; customers keep the label */}
                                        <FaHistory className={isAdmin ? 'text-xs xl:text-[10px]' : 'text-[10px]'} />
                                        <span className={isAdmin ? 'sr-only xl:not-sr-only xl:whitespace-nowrap' : undefined}>{t('nav.history')}</span>
                                        {userUnreadCount > 0 && (
                                            <span style={{
                                                position: 'absolute', top: '-5px', right: '-5px',
                                                minWidth: '16px', height: '16px',
                                                background: '#EF4444', borderRadius: '99px',
                                                fontSize: '9px', fontWeight: '800', color: '#fff',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                padding: '0 3px', boxShadow: '0 0 0 2px rgba(15,23,42,0.95)',
                                                animation: 'navBadgePulse 2s ease-in-out infinite',
                                            }}>
                                                {userUnreadCount > 99 ? '99+' : userUnreadCount}
                                            </span>
                                        )}
                                    </Link>

                                    {/* Divider */}
                                    <div className="w-px h-6 mx-1 flex-shrink-0" style={{ background: 'rgba(255,255,255,0.1)' }} />

                                    {/* User Avatar & Name (name hidden at lg, shown + truncating from xl). min-w-0 only from xl: at lg
                                        nothing inside can shrink, so a collapsing box would draw the logout icon over the toggle. */}
                                    <div className="flex items-center gap-2.5 pl-1 xl:min-w-0">
                                        <div
                                            title={userName}
                                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                                            style={{
                                                background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                                                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                                            }}
                                        >
                                            {userInitial}
                                        </div>
                                        <span className="hidden xl:block min-w-0 text-sm font-medium max-w-24 truncate" style={{ color: 'rgba(255,255,255,0.8)' }}>
                                            {userName}
                                        </span>
                                        <button
                                            onClick={onLogoutClick}
                                            className="p-1.5 rounded-lg transition-all duration-200"
                                            style={{ color: 'rgba(255,255,255,0.3)' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.color = '#F87171'; e.currentTarget.style.background = 'rgba(248, 113, 113, 0.1)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; e.currentTarget.style.background = 'transparent'; }}
                                            title={t('nav.logout')}
                                        >
                                            <FaSignOutAlt className="text-xs" />
                                        </button>
                                    </div>
                                    {/* Language Toggle (logged in) */}
                                    <button
                                        onClick={toggleLang}
                                        className="px-2.5 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200"
                                        style={{
                                            color: '#A5B4FC',
                                            background: 'rgba(99, 102, 241, 0.1)',
                                            border: '1px solid rgba(99, 102, 241, 0.2)',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(99, 102, 241, 0.25)';
                                            e.currentTarget.style.color = '#fff';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
                                            e.currentTarget.style.color = '#A5B4FC';
                                        }}
                                        title={lang === 'th' ? 'Switch to English' : 'เปลี่ยนเป็นภาษาไทย'}
                                    >
                                        {lang === 'th' ? '🇹🇭 TH' : '🇺🇸 EN'}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 xl:gap-2.5">
                                    <button
                                        onClick={onLoginClick}
                                        className="px-3 xl:px-4 py-2 text-sm font-medium whitespace-nowrap transition-all duration-200"
                                        style={{ color: 'rgba(255,255,255,0.6)' }}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                                    >
                                        {t('nav.login')}
                                    </button>
                                    <button
                                        onClick={onLoginClick}
                                        className="px-4 xl:px-5 py-2 rounded-lg text-sm font-bold text-white whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
                                        style={{
                                            background: 'linear-gradient(135deg, #3B82F6, #6366F1)',
                                            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.35)',
                                        }}
                                    >
                                        {t('nav.register')}
                                    </button>
                                    {/* Language Toggle (logged out) */}
                                    <button
                                        onClick={toggleLang}
                                        className="px-2.5 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200"
                                        style={{
                                            color: '#A5B4FC',
                                            background: 'rgba(99, 102, 241, 0.1)',
                                            border: '1px solid rgba(99, 102, 241, 0.2)',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(99, 102, 241, 0.25)';
                                            e.currentTarget.style.color = '#fff';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
                                            e.currentTarget.style.color = '#A5B4FC';
                                        }}
                                    >
                                        {lang === 'th' ? '🇹🇭 TH' : '🇺🇸 EN'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Mobile Menu Button — ml-auto pins it to the right edge (nav/right section are hidden below lg) */}
                        <button
                            onClick={() => setMobileMenuOpen(true)}
                            className="lg:hidden ml-auto p-2 rounded-lg transition-all duration-200"
                            style={{ color: 'rgba(255,255,255,0.6)' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.background = 'transparent'; }}
                        >
                            <FaBars className="text-lg" />
                        </button>
                    </div>
                </div >
            </header >

            {/* ===== MOBILE MENU — Portal to body ===== */}
            {
                createPortal(
                    <div className={`fixed inset-0 z-[9999] lg:hidden transition-all duration-300 ${mobileMenuOpen ? 'visible' : 'invisible'}`}>
                        {/* Backdrop */}
                        <div onClick={() => setMobileMenuOpen(false)} className={`absolute inset-0 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0'}`} style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)' }} />

                        {/* Drawer Panel */}
                        <div className={`absolute top-0 right-0 h-full w-[300px] max-w-[85vw] flex flex-col transition-transform duration-300 ease-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
                            style={{ background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)' }}>

                            {/* Header */}
                            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3B82F6, #6366F1)' }}>
                                        <FaGamepad className="text-white text-sm" />
                                    </div>
                                    <span className="font-bold text-white text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                        Fastlane<span style={{ color: '#818CF8' }}>Lv.</span>
                                    </span>
                                </div>
                                <button onClick={() => setMobileMenuOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all">
                                    <FaTimes className="text-sm" />
                                </button>
                            </div>

                            {/* Nav Items */}
                            <nav className="flex-1 overflow-y-auto px-3 py-3">
                                <div className="space-y-1">
                                    {navItems.map(item => item.special ? (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="nav-special-m relative flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-bold overflow-hidden"
                                            style={{
                                                background: isActive(item.to)
                                                    ? 'linear-gradient(135deg, rgba(245,158,11,0.26), rgba(236,72,153,0.26), rgba(139,92,246,0.26))'
                                                    : 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(236,72,153,0.12), rgba(139,92,246,0.12))',
                                                boxShadow: 'inset 0 0 0 1px rgba(236,72,153,0.4)',
                                            }}
                                            aria-current={isActive(item.to) ? 'page' : undefined}
                                        >
                                            <span className="text-base w-6 text-center">{item.emoji}</span>
                                            <span className="flex-1 nav-special-text">{item.label}</span>
                                            <span className="nav-special-badge nav-special-badge-m" aria-hidden="true">{lang === 'th' ? 'ใหม่' : 'NEW'}</span>
                                            <span className="nav-special-shine" aria-hidden="true" />
                                        </Link>
                                    ) : (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={`group flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${isActive(item.to)
                                                ? 'text-white'
                                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                                }`}
                                            style={isActive(item.to) ? {
                                                background: 'rgba(99, 102, 241, 0.15)',
                                                boxShadow: 'inset 0 0 0 1px rgba(99,102,241,0.25)'
                                            } : {}}
                                        >
                                            <span className="text-base w-6 text-center">{item.emoji}</span>
                                            <span className="flex-1">{item.label}</span>
                                            {isActive(item.to) && <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#818CF8' }} />}
                                        </Link>
                                    ))}

                                    {/* Divider + Extra Links */}
                                    {isLoggedIn && (
                                        <>
                                            <div className="my-2 mx-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} />
                                            <Link
                                                to="/history"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className={`group flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${isActive('/history')
                                                    ? 'text-white'
                                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                                    }`}
                                                style={isActive('/history') ? {
                                                    background: 'rgba(99, 102, 241, 0.15)',
                                                    boxShadow: 'inset 0 0 0 1px rgba(99,102,241,0.25)'
                                                } : {}}
                                            >
                                                <span className="text-base w-6 text-center">📋</span>
                                                <span className="flex-1">{t('nav.orderHistory')}</span>
                                                {userUnreadCount > 0 && (
                                                    <span className="min-w-[20px] h-5 flex items-center justify-center text-[10px] font-black text-white rounded-full px-1.5" style={{ background: '#EF4444', animation: 'navBadgePulse 2s ease-in-out infinite' }}>
                                                        {userUnreadCount > 99 ? '99+' : userUnreadCount}
                                                    </span>
                                                )}
                                                {isActive('/history') && <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#818CF8' }} />}
                                            </Link>

                                            {isAdmin && (
                                                <Link
                                                    to="/admin"
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className={`group flex items-center gap-3.5 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${isActive('/admin')
                                                        ? 'text-white'
                                                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                                                        }`}
                                                    style={isActive('/admin') ? {
                                                        background: 'rgba(99, 102, 241, 0.15)',
                                                        boxShadow: 'inset 0 0 0 1px rgba(99,102,241,0.25)'
                                                    } : {}}
                                                >
                                                    <span className="text-base w-6 text-center">⚙️</span>
                                                    <span className="flex-1">{t('nav.dashboard')}</span>
                                                    {adminUnreadCount > 0 && (
                                                        <span className="min-w-[20px] h-5 flex items-center justify-center text-[10px] font-black text-white rounded-full px-1.5" style={{ background: '#EF4444', animation: 'navBadgePulse 2s ease-in-out infinite' }}>
                                                            {adminUnreadCount > 99 ? '99+' : adminUnreadCount}
                                                        </span>
                                                    )}
                                                    {isActive('/admin') && <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#818CF8' }} />}
                                                </Link>
                                            )}
                                        </>
                                    )}
                                </div>
                            </nav>

                            {/* Language Toggle - Mobile */}
                            <div className="px-4 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                <button
                                    onClick={toggleLang}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
                                    style={{
                                        color: '#A5B4FC',
                                        background: 'rgba(99, 102, 241, 0.1)',
                                        border: '1px solid rgba(99, 102, 241, 0.2)',
                                    }}
                                >
                                    <FaGlobe className="text-xs" />
                                    {lang === 'th' ? '🇹🇭 ไทย → 🇺🇸 English' : '🇺🇸 English → 🇹🇭 Thai'}
                                </button>
                            </div>

                            {/* Bottom User Section */}
                            <div className="px-4 pb-6 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                {isLoggedIn ? (
                                    <div className="space-y-3">
                                        {/* User Profile Card */}
                                        <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)' }}>
                                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}>
                                                {userInitial}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-white text-sm font-semibold truncate">{userName}</p>
                                                <p className="text-gray-500 text-xs truncate">{user.email}</p>
                                            </div>
                                            <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                                        </div>

                                        {/* Logout Button */}
                                        <button
                                            onClick={() => { onLogoutClick(); setMobileMenuOpen(false); }}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 text-red-400 hover:text-red-300"
                                            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}
                                        >
                                            <FaSignOutAlt className="text-xs" />
                                            {t('nav.logout')}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-2.5">
                                        <button
                                            onClick={() => { onLoginClick(); setMobileMenuOpen(false); }}
                                            className="w-full px-4 py-3 rounded-xl text-sm font-bold text-white transition-all duration-200 hover:-translate-y-0.5"
                                            style={{ background: 'linear-gradient(135deg, #3B82F6, #6366F1)', boxShadow: '0 4px 16px rgba(99,102,241,0.3)' }}
                                        >
                                            {t('nav.loginOrRegister')}
                                        </button>
                                        <p className="text-center text-gray-600 text-xs">
                                            {t('nav.loginToStart')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )
            }
        <style>{`
            @keyframes navBadgePulse {
                0%, 100% { box-shadow: 0 0 0 2px rgba(15,23,42,0.95), 0 0 0 0 rgba(239,68,68,0.5); }
                50% { box-shadow: 0 0 0 2px rgba(15,23,42,0.95), 0 0 0 5px rgba(239,68,68,0); }
            }

            /* ── Highlighted menu entry (บอร์ดงาน) ── */
            .nav-special {
                background: linear-gradient(90deg, #F59E0B, #EC4899, #8B5CF6, #3B82F6, #F59E0B);
                background-size: 300% 100%;
                animation: navSpecialFlow 6s linear infinite;
                box-shadow: 0 0 14px rgba(236,72,153,0.35), 0 0 2px rgba(245,158,11,0.4);
                transition: transform .2s ease, box-shadow .2s ease;
            }
            .nav-special:hover { transform: translateY(-1px); box-shadow: 0 0 22px rgba(236,72,153,0.55), 0 0 6px rgba(245,158,11,0.5); }
            .nav-special:focus-visible { outline: 2px solid #F9A8D4; outline-offset: 3px; }
            .nav-special-text {
                background: linear-gradient(90deg, #FDE68A, #F9A8D4, #C4B5FD);
                -webkit-background-clip: text; background-clip: text; color: transparent;
            }
            .nav-special-icon { color: #FCD34D; filter: drop-shadow(0 0 4px rgba(252,211,77,0.6)); }
            .nav-special-shine {
                position: absolute; top: 0; left: -60%; width: 40%; height: 100%; pointer-events: none;
                background: linear-gradient(100deg, transparent, rgba(255,255,255,0.28), transparent);
                transform: skewX(-20deg);
                animation: navSpecialShine 4s ease-in-out infinite;
            }
            .nav-special-badge {
                position: absolute; top: -8px; right: -9px;
                font-size: 9px; font-weight: 900; line-height: 1; letter-spacing: .02em;
                padding: 3px 6px; border-radius: 999px; color: #fff;
                background: linear-gradient(135deg, #F43F5E, #F97316);
                box-shadow: 0 0 0 2px rgba(15,23,42,0.95), 0 0 10px rgba(244,63,94,0.6);
                animation: navSpecialBadge 2.2s ease-in-out infinite;
            }
            .nav-special-badge-m { position: static; box-shadow: 0 0 10px rgba(244,63,94,0.5); }
            @keyframes navSpecialFlow { 0% { background-position: 0% 50%; } 100% { background-position: 300% 50%; } }
            @keyframes navSpecialShine { 0%, 55% { left: -60%; } 100% { left: 130%; } }
            @keyframes navSpecialBadge { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
            @media (prefers-reduced-motion: reduce) {
                .nav-special, .nav-special-badge { animation: none; }
                .nav-special-shine { display: none; }
            }
        `}</style>
        </>
    );
}

export default Navbar;
